import { AxiosResponse } from 'axios';

import apiClient from './client';
import i18n from 'i18n';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { CreateErganiSettings, ErganiBranchAssociation, ErganiBranches, ErganiEmployerInfo } from 'interfaces/ergani';
import { ERGANI_ERROR_STATUS, erganiRequest, isErganiApiError } from './erganiCommon';

export { ErganiApiError, getErganiErrorMessage, isErganiApiError } from './erganiCommon';

interface ErganiService {
  getUsername(): Promise<string | null>;
  upsertCredentials(postData: CreateErganiSettings): Promise<void>;
  getEmployer(): Promise<ErganiEmployerInfo>;
  getBranches(): Promise<ErganiBranches>;
  getAssociations(): Promise<ErganiBranchAssociation[]>;
  upsertAssociation(association: ErganiBranchAssociation): Promise<ErganiBranchAssociation>;
  deleteAssociation(serialNumber: string): Promise<void>;
}

const endpoints = {
  getUsername: '/ergani/authenticate/username',
  upsertCredentials: '/ergani/authenticate',
  employer: '/ergani/employer',
  branches: '/ergani/branches',
  associations: '/ergani/branches/associations',
  association: (serialNumber: string) => `/ergani/branches/associations/${encodeURIComponent(serialNumber)}`,
};

export const erganiServices: ErganiService = {
  getUsername: async (): Promise<string | null> => {
    try {
      const username = await erganiRequest<string>(() => apiClient.get(endpoints.getUsername));
      return username || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  upsertCredentials: async (postData: CreateErganiSettings): Promise<void> => {
    try {
      await erganiRequest<void>(() => apiClient.put(endpoints.upsertCredentials, postData));
    } catch (error: any) {
      if (isErganiApiError(error)) {
        throw error;
      }

      if (error.response?.status === 400) {
        throw new Error(i18n.t('apiErrors.erganiInvalidCredentials'));
      }

      if (error.response?.status === ERGANI_ERROR_STATUS) {
        throw new Error(i18n.t('apiErrors.erganiUnavailable'));
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors[0].message);
      }

      throw error;
    }
  },

  getEmployer: (): Promise<ErganiEmployerInfo> => erganiRequest(() => apiClient.get(endpoints.employer)),

  getBranches: (): Promise<ErganiBranches> => erganiRequest(() => apiClient.get(endpoints.branches)),

  getAssociations: async (): Promise<ErganiBranchAssociation[]> => {
    const response: AxiosResponse<ApiResponseDto<ErganiBranchAssociation[]>> = await apiClient.get(endpoints.associations);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data ?? [];
  },

  upsertAssociation: async (association: ErganiBranchAssociation): Promise<ErganiBranchAssociation> => {
    try {
      const response: AxiosResponse<ApiResponseDto<ErganiBranchAssociation>> = await apiClient.put(endpoints.associations, association);

      if (response.data.errors) {
        throw new Error(response.data.errors.map(error => error.message).join(', '));
      }

      return response.data.data!;
    } catch (error: any) {
      if (error.response?.status === 409) {
        throw new Error(i18n.t('apiErrors.erganiBranchAlreadyAssociated'));
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors[0].message);
      }

      throw error;
    }
  },

  deleteAssociation: async (serialNumber: string): Promise<void> => {
    const response: AxiosResponse<ApiResponseDto<void>> = await apiClient.delete(endpoints.association(serialNumber));

    if (response.data?.errors) {
      throw new Error(response.data.errors.map(error => error.message).join(', '));
    }
  },
};
